import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { createPaymentIntent, confirmPayment } from '../services/paymentsService';
import { listEvents, getEvent } from '../services/eventsService';
import { createTransaction } from '../services/transactionsService';
import { listVendors } from '../services/vendorsService';
import { getUserProfile } from '../services/profilesService';
import './ProfilePage.css';
import { useAuth } from '../context/AuthContext';

// Stripe publishable key is loaded from the selected event's `bazaarPaymentInfo.clientId`.

const normalizeConfiguredValue = (value) => {
	const normalized = String(value || '').trim();
	return normalized.toLowerCase() === 'na' ? '' : normalized;
};

const PaymentForm = ({ initialAmount = '0.00' }) => {
	const navigate = useNavigate();
	// stripe/elements not needed here — card entry is performed on the Checkout page

	const [amount, setAmount] = useState(initialAmount);
	const amountInputRef = useRef(null);
	const currency = 'usd';
	const [description, setDescription] = useState('Payment');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [success, setSuccess] = useState('');
	const [paymentIntentId, setPaymentIntentId] = useState('');
	const [clientSecret, setClientSecret] = useState('');
	const [processing, setProcessing] = useState(false);
  
	// Metadata fields
	const [payeeName, setPayeeName] = useState('');
	const [payeeStreet, setPayeeStreet] = useState('');
	const [payeeEmail, setPayeeEmail] = useState('');
	const [payeePhone, setPayeePhone] = useState('');
	const [payeeZip, setPayeeZip] = useState('');
	const [payeeCity, setPayeeCity] = useState('');
	const [payeeState, setPayeeState] = useState('');
	const [payeeCountry, setPayeeCountry] = useState('');
	const [vendorName, setVendorName] = useState('');
	const [vendorNumber, setVendorNumber] = useState('');
	const [eventName, setEventName] = useState('');
	const [eventId, setEventId] = useState('');
	const [stripePromise, setStripePromise] = useState(null);
	const [events, setEvents] = useState([]);
	const [eventsLoading, setEventsLoading] = useState(false);

	const [vendors, setVendors] = useState([]);
	const [vendorsLoading, setVendorsLoading] = useState(false);
	const [vendorIdSelected, setVendorIdSelected] = useState('');
	// Payment reason: default to MANUAL and only expose that option for now
	const [paymentReason, setPaymentReason] = useState('MANUAL');
	const paymentReasonOptions = [
		{ code: 'MANUAL', label: 'Manual' },
	];

	// Processor info (user who is handling the payment)
	const { currentUser } = useAuth();
	const [processorName, setProcessorName] = useState('');
	const [otherReason, setOtherReason] = useState('');

	// Load events for the Event dropdown (role-aware; only published layouts)
	useEffect(() => {
		let cancelled = false;
		(async () => {
			setEventsLoading(true);
			try {
				const all = await listEvents();
				const havingLayout = (all || []).filter(ev => ev?.layoutId && (ev?.status || '').toLowerCase() !== 'archived');

				// Apply role-based filtering: Admin sees all, EventAdmin checks `eventAdmins`, Organizer checks `organizers`, others see none
				let filteredHaving = havingLayout;
				try {
					if (currentUser && currentUser.uid) {
						try {
							const prof = await getUserProfile(currentUser.uid);
							const uidLower = String(currentUser.uid || '').toLowerCase();
							if (prof?.userType === 'EventAdmin') {
								filteredHaving = (havingLayout || []).filter(ev => Array.isArray(ev.eventAdmins) && ev.eventAdmins.some(a => String(a || '').toLowerCase() === uidLower));
							} else if (prof?.userType === 'Organizer') {
								filteredHaving = (havingLayout || []).filter(ev => Array.isArray(ev.organizers) && ev.organizers.some(a => String(a || '').toLowerCase() === uidLower));
							} else if (prof?.userType !== 'Admin') {
								filteredHaving = [];
							}
						} catch (profileErr) {
							filteredHaving = [];
						}
					}
				} catch (e) {
					filteredHaving = [];
				}

				// No per-event layout fetch here — trust event's layoutId and status
				const entries = (filteredHaving || []).map(ev => ({ id: ev.id, name: ev.name || ev.id, layoutId: ev.layoutId }));
				const filtered = (entries || []).sort((a, b) => String(a.name).localeCompare(String(b.name)));
				if (!cancelled) {
					setEvents(filtered);
				}
			} catch (err) {
				console.error('Failed to load events for ManualPaymentPage:', err);
			} finally {
				if (!cancelled) setEventsLoading(false);
			}
		})();
		return () => { cancelled = true; };
	}, [currentUser]);

	// When an event is selected, load the event and initialize Stripe using
	// the event's `bazaarPaymentInfo.clientId` (publishable key).
	useEffect(() => {
		let cancelled = false;
		if (!eventId) {
			setStripePromise(null);
			return () => { cancelled = true; };
		}
		(async () => {
			try {
				const ev = await getEvent(eventId);
				const clientId = normalizeConfiguredValue(ev?.bazaarPaymentInfo?.clientId);
				if (!cancelled) {
					setStripePromise(clientId ? loadStripe(clientId) : null);
				}
			} catch (e) {
				if (!cancelled) setStripePromise(null);
			}
		})();
		return () => { cancelled = true; };
	}, [eventId]);

	// Load vendors for the Vendor dropdown
	useEffect(() => {
		let cancelled = false;
		(async () => {
			setVendorsLoading(true);
			try {
				const list = await listVendors();
				if (!cancelled) {
					const sorted = Array.isArray(list)
						? list.slice().sort((a, b) => {
							const an = (a?.name || '').toLowerCase();
							const bn = (b?.name || '').toLowerCase();
							return an.localeCompare(bn);
						})
						: [];
					setVendors(sorted);
					// Do not auto-select a vendor; user must choose from dropdown
				}
			} catch (err) {
				console.error('Failed to load vendors for ManualPaymentPage:', err);
			} finally {
				if (!cancelled) setVendorsLoading(false);
			}
		})();
		return () => { cancelled = true; };
	}, []);
	// Comments for the payment (required)
	const [paymentComments, setPaymentComments] = useState('');

	// Prefill processor info: prefer canonical profile name, fall back to auth displayName/email
	useEffect(() => {
		let cancelled = false;
		(async () => {
			if (!currentUser || !currentUser.uid) return;
			try {
				const prof = await getUserProfile(currentUser.uid);
				if (cancelled) return;
				if (prof) {
					const nameParts = [];
					if (prof.firstName) nameParts.push(prof.firstName);
					if (prof.lastName) nameParts.push(prof.lastName);
					const full = nameParts.length > 0 ? nameParts.join(' ') : (prof.name || prof.displayName || prof.email || null);
					if (full) { setProcessorName(full); return; }
				}
				// Fallback to auth user fields
				setProcessorName(currentUser.displayName || currentUser.email || '');
			} catch (e) {
				setProcessorName(currentUser.displayName || currentUser.email || '');
			}
		})();
		return () => { cancelled = true; };
	}, [currentUser]);

	// Single entry point to start checkout: creates a PaymentIntent if needed,
	// stores the id/secret locally, then navigates to `/checkout` with state.
	const setAmountCaretToStart = () => {
		window.requestAnimationFrame(() => {
			const input = amountInputRef.current;
			if (!input) return;
			try {
				input.setSelectionRange(0, 0);
			} catch (e) {
				// Some browsers may not support selection APIs for number inputs.
			}
		});
	};

	const handleAmountFocus = () => {
		const normalized = String(amount || '').trim();
		if (normalized === '0.00' || normalized === '0' || normalized === '0.0') {
			setAmount('');
		}
		setAmountCaretToStart();
	};

	const handleInitiateCheckout = async (e) => {
		if (e && e.preventDefault) e.preventDefault();
		setError('');
		// Basic validation before navigating
		if (!eventId) {
			setError('Please select an event for this payment');
			return;
		}
		if (!vendorIdSelected) {
			setError('Please select a vendor for this payment');
			return;
		}

		const dynamicDescription = `${eventName}-BAZAAR-${vendorName}-Manual`;

		// Validate that the selected event has a publishable key and that Stripe can initialize.
		try {
			const evForKey = await getEvent(eventId);
			const eventKey = normalizeConfiguredValue(evForKey?.bazaarPaymentInfo?.clientId);
			if (!eventKey) {
				setError('Selected event does not have a Stripe publishable key configured');
				return;
			}
			try {
				const stripeTest = await loadStripe(eventKey.trim());
				if (!stripeTest) {
					setError('Failed to initialize Stripe with event publishable key');
					return;
				}
			} catch (e) {
				setError('Failed to initialize Stripe with event publishable key: ' + (e?.message || e));
				return;
			}
		} catch (e) {
			setError('Failed to validate event Stripe configuration: ' + (e?.message || e));
			return;
		}

		// If we already have a payment intent, navigate immediately
		// if (paymentIntentId && clientSecret) {
		// 	console.log('Initiating checkout (existing intent)', { paymentIntentId, eventId, amount });
		// 	navigate('/checkout', {
		// 		state: {
		// 			orderData: {
		// 				amount: Math.round(parseFloat(amount) * 100),
		// 				currency: 'usd',
		// 				description: dynamicDescription,
		// 				eventName: eventName,
		// 				eventId: eventId,
		// 				comments: paymentComments,
		// 				metadata: {
		// 					source: 'manual-payment-page',
		// 					payeeName,
		// 					payeeZip,
		// 					payeeCity,
		// 					payeeState,
		// 					payeeCountry,
		// 					payeeEmail,
		// 					payeePhone,
		// 					payeeStreet,
		// 					vendorName,
		// 					vendorNumber,
		// 					eventName,
		// 					eventId,
		// 					purchaseType: 'manual',
		// 					comments: paymentComments,
		// 					paymentReason,
		// 					processorName,
		// 				}
		// 			},
		// 			paymentIntentId: paymentIntentId,
		// 			clientSecret: clientSecret
		// 		}
		// 	});
		// 	return;
		// }

		// Otherwise create a PaymentIntent then navigate
		setLoading(true);
		try {
			let paymentMethodConfiguration = '';
			try {
				if (eventId) {
					const selectedEvent = await getEvent(eventId);
					paymentMethodConfiguration = normalizeConfiguredValue(selectedEvent?.bazaarPaymentInfo?.paymentConfigId);
				}
			} catch (cfgErr) {
				console.warn('[ManualPaymentPage] Failed to resolve payment config ID:', cfgErr);
			}

			const result = await createPaymentIntent({
				amount: Math.round(parseFloat(amount) * 100),
				currency: 'usd',
				description: dynamicDescription,
				metadata: {
					source: 'manual-payment-page',
					payeeName,
					payeeZip,
					payeeEmail,
					payeePhone,
					vendorName,
					vendorNumber,
					eventName,
					eventId,
					purchaseType: 'manual',
					comments: paymentComments,
					paymentReason,
					processorName,
				},
				eventId: eventId,
				payment_method_types: ['card', 'amazon_pay'],
				payment_method_configuration: paymentMethodConfiguration,
			});
				if (result) {
					console.log('Initiating checkout (new intent)', { paymentIntentId: result.paymentIntentId || result.id, eventId, amount });
				const newPaymentIntentId = result.paymentIntentId || result.id || '';
				const newClientSecret = result.clientSecret || result.client_secret || '';

				// Best-effort logging: create manual transaction with available context.
				if (currentUser?.uid && newPaymentIntentId) {
					try {
						await createTransaction({
							paymentIntentId: newPaymentIntentId,
							profileId: currentUser.uid,
							eventId: eventId || '',
							amount: Math.round(parseFloat(amount) * 100),
							currency: 'usd',
							status: 'incomplete',
							metadata: {
								source: 'manual-payment-page',
								payeeName,
								payeeZip,
								payeeCity,
								payeeState,
								payeeCountry,
								payeeEmail,
								payeePhone,
								payeeStreet,
								vendorName,
								vendorNumber,
								eventName,
								eventId,
								purchaseType: 'manual',
								comments: paymentComments,
								paymentReason,
								processorName,
							},
							booths: [],
						});
					} catch (txError) {
						console.error('[ManualPaymentPage] Failed to create manual transaction:', txError);
					}
				}

				setPaymentIntentId(newPaymentIntentId);
				setClientSecret(newClientSecret);
				navigate('/checkout', {
					state: {
						orderData: {
							amount: Math.round(parseFloat(amount) * 100),
							currency: 'usd',
							description: dynamicDescription,
							eventName: eventName,
							eventId: eventId,
							comments: paymentComments,
							metadata: {
								source: 'manual-payment-page',
								payeeName,
								payeeZip,
								payeeCity,
								payeeState,
								payeeCountry,
								payeeEmail,
								payeePhone,
								payeeStreet,
								vendorName,
								vendorNumber,
								eventName,
								eventId,
								purchaseType: 'manual',
								comments: paymentComments,
								paymentReason,
								processorName,
							}
						},
						paymentIntentId: newPaymentIntentId,
						clientSecret: newClientSecret
					}
				});
			}
		} catch (err) {
			setError(err.message || 'Failed to create payment intent');
		} finally {
			setLoading(false);
		}
	};

	// Navigate to Checkout and let the Checkout page create the PaymentIntent.
	// This is used when you want to go straight to Checkout (no local PaymentIntent created here).
	// const handleStartCheckout = async (e) => {
	// 	if (e && e.preventDefault) e.preventDefault();
	// 	setError('');
	// 	// Basic validation before navigating
	// 	if (!eventId) {
	// 		setError('Please select an event for this payment');
	// 		return;
	// 	}
	// 	if (!vendorIdSelected) {
	// 		setError('Please select a vendor for this payment');
	// 		return;
	// 	}

	// 	const dynamicDescription = `${eventName}-BAZAAR-${vendorName}-Manual`;

	// 	// If we already have a payment intent, navigate immediately
	// 	if (paymentIntentId && clientSecret) {
	// 		navigate('/checkout', {
	// 			state: {
	// 				orderData: {
	// 					amount: Math.round(parseFloat(amount) * 100),
	// 					currency: 'usd',
	// 					description: dynamicDescription,
	// 					eventName: eventName,
	// 					eventId: eventId,
	// 					comments: paymentComments,
	// 					metadata: {
	// 						source: 'manual-payment-page',
	// 						payeeName,
	// 						payeeZip,
	// 						payeeCity,
	// 						payeeState,
	// 						payeeCountry,
	// 						payeeEmail,
	// 						payeePhone,
	// 						payeeStreet,
	// 						vendorName,
	// 						vendorNumber,
	// 						eventName,
	// 						eventId,
	// 						purchaseType: 'manual',
	// 						comments: paymentComments,
	// 						paymentReason,
	// 						processorName,
	// 					}
	// 				},
	// 				paymentIntentId: paymentIntentId,
	// 				clientSecret: clientSecret
	// 			}
	// 		});
	// 		return;
	// 	}

	// 	// Otherwise create a PaymentIntent then navigate
	// 	setLoading(true);
	// 	try {
	// 		const result = await createPaymentIntent({
	// 			amount: Math.round(parseFloat(amount) * 100),
	// 			currency: 'usd',
	// 			description: dynamicDescription,
	// 			metadata: {
	// 				source: 'manual-payment-page',
	// 				payeeName,
	// 				payeeZip,
	// 				payeeEmail,
	// 				payeePhone,
	// 				vendorName,
	// 				vendorNumber,
	// 				eventName,
	// 				eventId,
	// 				purchaseType: 'manual',
	// 				comments: paymentComments,
	// 				paymentReason,
	// 				processorName,
	// 			}
	// 		});
	// 		if (result) {
	// 			const newPaymentIntentId = result.paymentIntentId || result.id || '';
	// 			const newClientSecret = result.clientSecret || result.client_secret || '';
	// 			setPaymentIntentId(newPaymentIntentId);
	// 			setClientSecret(newClientSecret);
	// 			navigate('/checkout', {
	// 				state: {
	// 					orderData: {
	// 						amount: Math.round(parseFloat(amount) * 100),
	// 						currency: 'usd',
	// 						description: dynamicDescription,
	// 						eventName: eventName,
	// 						eventId: eventId,
	// 						comments: paymentComments,
	// 						metadata: {
	// 							source: 'manual-payment-page',
	// 							payeeName,
	// 							payeeZip,
	// 							payeeCity,
	// 							payeeState,
	// 							payeeCountry,
	// 							payeeEmail,
	// 							payeePhone,
	// 							payeeStreet,
	// 							vendorName,
	// 							vendorNumber,
	// 							eventName,
	// 							eventId,
	// 							purchaseType: 'manual',
	// 							comments: paymentComments,
	// 							paymentReason,
	// 							processorName,
	// 						}
	// 					},
	// 					paymentIntentId: newPaymentIntentId,
	// 					clientSecret: newClientSecret
	// 				}
	// 			});
	// 		}
	// 	} catch (err) {
	// 		setError(err.message || 'Failed to create payment intent');
	// 	} finally {
	// 		setLoading(false);
	// 	}
	// };
  
	const noVendors = !vendorsLoading && (!Array.isArray(vendors) || vendors.length === 0);
	const noEvents = !eventsLoading && (!Array.isArray(events) || events.length === 0);

	const pageContent = (
		<div className="profile-container" style={{ color: '#333' }}>
			<div className="profile-header-row">
				<div style={{ flex: 1 }} />
				<button type="button" className="cancel-btn" onClick={() => navigate('/')}>Close</button>
			</div>

			<h2 className="profile-title" style={{ color: '#333' }}>Make a Payment</h2>
      
			<div className="profile-hint" style={{ marginBottom: 20, color: '#666' }}>
				Enter the amount and card details below to charge a credit card. This page creates a Payment Intent via your backend and confirms the card payment client-side using Stripe Elements.
			</div>

			{error && (
				<div className="profile-error" style={{ marginBottom: 16 }}>{error}</div>
			)}

			{success && (
				<div style={{ padding: '12px', marginBottom: '16px', backgroundColor: '#d4edda', color: '#155724', border: '1px solid #c3e6cb', borderRadius: '4px' }}>{success}</div>
			)}

			<form onSubmit={(e) => handleInitiateCheckout(e)}>
				<div className="profile-grid">
					{/* Event goes first */}
					<div>
						<label className="profile-label" style={{ color: '#333' }}>Event</label>
						{eventsLoading ? (
							<div className="profile-hint">Loading events...</div>
						) : Array.isArray(events) && events.length > 0 ? (
							<select
								className="profile-input"
								value={eventId}
								onChange={(e) => {
								const sel = e.target.value;
								setEventId(sel);
								const ev = events.find(x => x.id === sel);
								setEventName(ev ? (ev.name || ev.id) : '');
								// when selecting event, clear any previously created payment intent info
								setPaymentIntentId('');
								setClientSecret('');
							}}
								required
								style={{ color: '#333' }}
							>
								<option value="" disabled>-- Select event --</option>
								{events.map(ev => (
									<option key={ev.id} value={ev.id}>{ev.name || ev.id}</option>
								))}
							</select>
						) : (
							<div className="profile-hint" style={{ color: '#d9534f' }}>No events found — add events in admin before taking payments</div>
						)}
					</div>

					{/* Vendor selection: required — must choose existing vendor */}
					<div>
						<label className="profile-label" style={{ color: '#333' }}>Vendor</label>
						{vendorsLoading ? (
							<div className="profile-hint">Loading vendors...</div>
						) : Array.isArray(vendors) && vendors.length > 0 ? (
							<select
								className="profile-input"
								value={vendorIdSelected}
								onChange={(e) => {
								const sel = e.target.value;
								setVendorIdSelected(sel);
								const v = vendors.find(x => x.id === sel);
								setVendorName(v ? (v.name || '') : '');
								setVendorNumber(v ? (v.vendorNumber || '') : '');
								// Prefill payee contact fields from vendor record (editable by user)
								setPayeeStreet(v ? (v.addressStreet || '') : payeeStreet);
								setPayeeName(v ? (v.name || '') : payeeName);
								setPayeeEmail(v ? (v.email || '') : payeeEmail);
								setPayeePhone(v ? (v.phone || '') : payeePhone);
								setPayeeZip(v ? (v.addressZip || '') : payeeZip);
								setPayeeCity(v ? (v.addressCity || '') : payeeCity);
								setPayeeState(v ? (v.addressState || '') : payeeState);
								setPayeeCountry(v ? (v.addressCountry || 'US') : payeeCountry);
							}}
								required
								style={{ color: '#333' }}
							>
								<option value="" disabled>-- Select vendor --</option>
								{vendors.map(v => (
									<option key={v.id} value={v.id}>{v.name || v.id} {v.vendorNumber ? `(${v.vendorNumber})` : ''}</option>
								))}
							</select>
						) : (
							<div className="profile-hint" style={{ color: '#d9534f' }}>No vendors found — add vendors in admin before taking payments</div>
						)}
					</div>

					<div>
						<label className="profile-label" style={{ color: '#333' }}>Amount (USD)</label>
						<input
							ref={amountInputRef}
							type="number"
							step="0.01"
							min="0.50"
							className="profile-input"
							value={amount}
							onChange={(e) => setAmount(e.target.value)}
							onFocus={handleAmountFocus}
							onClick={setAmountCaretToStart}
							required
							style={{ color: '#333' }}
						/>
						<div className="profile-hint" style={{ color: '#666' }}>Minimum $0.50</div>
					</div>

					{/* Payment Reason dropdown (required) */}
					<div>
						<label className="profile-label" style={{ color: '#333' }}>Payment Reason</label>
						<select className="profile-input" value={paymentReason} onChange={(e) => setPaymentReason(e.target.value)} style={{ color: '#333' }}>
							{paymentReasonOptions.map(opt => (
								<option key={opt.code} value={opt.code}>{opt.label}</option>
							))}
						</select>
					</div>

					<div style={{ gridColumn: '1 / -1', marginTop: 16 }}>
						<h3 style={{ marginBottom: 12, color: '#333', fontSize: '1.1em' }}>Payer & Metadata</h3>
						<div className="profile-hint" style={{ color: '#666', marginBottom: 12 }}>Description will be auto-generated as: {eventName}-BAZAAR-{vendorName}-Manual</div>
					</div>

					<div>
						<label className="profile-label" style={{ color: '#333' }}>Payee Name</label>
						<input type="text" className="profile-input" value={payeeName} onChange={(e) => setPayeeName(e.target.value)} placeholder="Enter name" style={{ color: '#333' }} />
					</div>

					<div>
						<label className="profile-label" style={{ color: '#333' }}>Payee Email</label>
						<input type="email" className="profile-input" value={payeeEmail} onChange={(e) => setPayeeEmail(e.target.value)} placeholder="Enter email" style={{ color: '#333' }} />
					</div>

					<div>
						<label className="profile-label" style={{ color: '#333' }}>Payee Phone</label>
						<input type="tel" className="profile-input" value={payeePhone} onChange={(e) => setPayeePhone(e.target.value)} placeholder="Enter phone number" style={{ color: '#333' }} />
						<div className="profile-hint" style={{ color: '#666' }}>Format: +1234567890</div>
					</div>

					<div>
						<label className="profile-label" style={{ color: '#333' }}>Payee Street</label>
						<input type="text" className="profile-input" value={payeeStreet} onChange={(e) => setPayeeStreet(e.target.value)} placeholder="Enter street address" style={{ color: '#333' }} />
						<div className="profile-hint" style={{ color: '#666' }}>Street/line 1 for billing address (editable)</div>
					</div>

					<div>
						<label className="profile-label" style={{ color: '#333' }}>Payee City</label>
						<input type="text" className="profile-input" value={payeeCity} onChange={(e) => setPayeeCity(e.target.value)} placeholder="Enter city" style={{ color: '#333' }} />
					</div>

					<div>
						<label className="profile-label" style={{ color: '#333' }}>Payee State</label>
						<input type="text" className="profile-input" value={payeeState} onChange={(e) => setPayeeState(e.target.value)} placeholder="Enter state" style={{ color: '#333' }} />
					</div>

					<div>
						<label className="profile-label" style={{ color: '#333' }}>Payee Zip</label>
						<input type="text" className="profile-input" value={payeeZip} onChange={(e) => setPayeeZip(e.target.value)} placeholder="Enter Zip code" style={{ color: '#333' }} />
					</div>

					<div>
						<label className="profile-label" style={{ color: '#333' }}>Payee Country</label>
						<input type="text" className="profile-input" value={payeeCountry} onChange={(e) => setPayeeCountry(e.target.value)} placeholder="Enter country (e.g. US)" style={{ color: '#333' }} />
					</div>

					{/* Processor info (who is processing this payment) */}
					<div>
						<label className="profile-label" style={{ color: '#333' }}>Payment Processed By</label>
						<input type="text" className="profile-input" value={processorName} readOnly placeholder="Your name" style={{ color: '#333', backgroundColor: '#f5f5f5' }} />
					</div>

					{/* Processor ID removed: checkout captures authenticated user ID, no need to show or pass it here */}

					{/* Vendor number is shown in the Vendor dropdown; no editable field here */}

					<div style={{ gridColumn: '1 / -1' }}>
						<label className="profile-label" style={{ color: '#333' }}>Payment Comments</label>
						<textarea
							className="profile-input"
							value={paymentComments}
							onChange={(e) => setPaymentComments(e.target.value)}
							placeholder="Enter payment comments (required)"
							required
							style={{ minHeight: 80, color: '#333' }}
						/>
						<div className="profile-hint" style={{ color: '#666' }}>Add any notes or comments for this payment (required)</div>
					</div>
				</div>

				<div style={{ marginTop: 20, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
					<button type="submit" className="back-btn" disabled={loading || processing || noVendors || noEvents} style={{ minWidth: 180, color: '#fff', backgroundColor: '#1976d2' }}>
						{loading || processing ? 'Processing...' : `Checkout -[USD: ${amount}]`}
					</button>



					{paymentIntentId && (
						<button type="button" className="back-btn" onClick={handleInitiateCheckout} disabled={loading} style={{ minWidth: 180, color: '#fff', backgroundColor: '#28a745' }}>Open Checkout →</button>
					)}
				</div>
			</form>

			{/* Card details are collected on the Checkout page — removed from manual page */}

			{paymentIntentId && (
				<div style={{ marginTop: 30 }}>
					<h3 style={{ marginBottom: 12, color: '#333' }}>Payment Details</h3>
					<div className="profile-grid">
						<div>
							<label className="profile-label" style={{ color: '#333' }}>Payment Intent ID</label>
							<input type="text" className="profile-input" value={paymentIntentId} readOnly style={{ fontFamily: 'monospace', fontSize: '0.9em', color: '#333' }} />
						</div>
						<div>
							<label className="profile-label" style={{ color: '#333' }}>Client Secret (truncated)</label>
							<input type="text" className="profile-input" value={clientSecret ? clientSecret.substring(0, 30) + '...' : ''} readOnly style={{ fontFamily: 'monospace', fontSize: '0.9em', color: '#333' }} />
						</div>
					</div>
				</div>
			)}

			{/* <div style={{ marginTop: 40, padding: 16, backgroundColor: '#d1ecf1', borderRadius: 4, border: '1px solid #bee5eb' }}>
				<strong style={{ color: '#0c5460' }}>🚀 Test Card Numbers:</strong>
				<ul style={{ color: '#0c5460', margin: '8px 0 0 20px', lineHeight: 1.6 }}>
					<li><code>4242 4242 4242 4242</code> - Visa (succeeds)</li>
					<li><code>4000 0025 0000 3155</code> - Visa (requires authentication)</li>
					<li><code>4000 0000 0000 9995</code> - Visa (always fails)</li>
				</ul>
				<p style={{ color: '#0c5460', margin: '8px 0 0 0' }}>Use any future expiry date and any 3-digit CVC.</p>
			</div> */}
		</div>
	);

	return stripePromise ? <Elements stripe={stripePromise}>{/* Elements used only if event provides publishable key */ pageContent}</Elements> : pageContent;
};

const StripePaymentPage = () => {
	return <PaymentForm />;
};

export default StripePaymentPage;
